import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { cn } from "@/lib/cn";
import { Icon } from "@/components/icons/Icon";

interface TopbarProps {
  title: string;
  breadcrumb?: ReactNode;
  right?: ReactNode;
  className?: string;
}

/** 상단 탑바 — 제목 + 브레드크럼 + 검색 + 기간 + 시계 + 알림 */
const Topbar = ({ title, breadcrumb, right, className }: TopbarProps) => {
  const [now, setNow] = useState(() => new Date());
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const refresh = useCallback(() => {
    if (refreshing) return;
    setRefreshing(true);
    setTimeout(() => {
      setNow(new Date());
      setRefreshing(false);
    }, 600);
  }, [refreshing]);

  const dateStr = useMemo(() => {
    const yyyy = now.getFullYear();
    const M = String(now.getMonth() + 1).padStart(2, "0");
    const D = String(now.getDate()).padStart(2, "0");
    const hh = String(now.getHours()).padStart(2, "0");
    const mm = String(now.getMinutes()).padStart(2, "0");
    return `${yyyy}.${M}.${D} ${hh}:${mm}`;
  }, [now]);

  return (
    <header
      className={cn(
        "sticky top-0 z-50 flex h-14 shrink-0 items-center gap-4 border-b border-border bg-white/85 px-7 backdrop-blur-sm",
        className,
      )}
    >
      {/* 제목 + 브레드크럼 */}
      <div className="flex min-w-0 items-baseline gap-2 whitespace-nowrap">
        <span className="text-[16.5px] font-bold tracking-[-0.015em] text-gray-950">
          {title}
        </span>
        {breadcrumb && (
          <>
            <span className="text-base leading-none text-gray-300">›</span>
            <span className="text-[15.5px] font-medium tracking-[-0.005em] text-gray-500">
              {breadcrumb}
            </span>
          </>
        )}
      </div>

      {/* 검색 */}
      <div className="relative ml-auto">
        <Icon
          name="search"
          size={14}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
        />
        <input
          className="h-8 w-70 rounded-[9px] border border-border bg-white pl-9 pr-3 text-[13px] text-gray-700 placeholder:text-gray-400 outline-none focus:border-primary/40 focus:ring-1 focus:ring-primary/20"
          placeholder="사고번호·피보험자명·접수번호 검색 (⌘K)"
        />
      </div>

      {right}

      {/* 최종 업데이트 */}
      <div
        className="ml-1 flex h-7 items-center gap-1.5 border-l border-border/60 pl-3 text-xs font-medium whitespace-nowrap text-gray-500"
        title={`최종 업데이트 ${dateStr}`}
      >
        <span className="tracking-[-0.005em]">최종 업데이트</span>
        <span className="font-mono text-xs font-semibold tabular-nums text-gray-700">
          {dateStr}
        </span>
        <button
          className={cn(
            "grid size-[22px] place-items-center rounded-[5px] text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-700",
            refreshing && "animate-spin",
          )}
          onClick={refresh}
          disabled={refreshing}
          title="새로고침"
          aria-label="새로고침"
        >
          <Icon name="refresh" size={12} />
        </button>
      </div>

      {/* 알림 */}
      <button
        className="relative grid size-[34px] place-items-center rounded-lg text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700"
        title="알림"
        aria-label="알림"
      >
        <Icon name="bell" size={16} />
        <span className="absolute right-2 top-[7px] size-[7px] rounded-full border-[1.5px] border-white bg-destructive" />
      </button>

      {/* 설정 */}
      <button
        className="grid size-[34px] place-items-center rounded-lg text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700"
        title="설정"
        aria-label="설정"
      >
        <Icon name="settings" size={16} />
      </button>
    </header>
  );
};

export { Topbar };
export type { TopbarProps };
